import { useState, useEffect, useMemo } from 'react';
import { recommendationsService } from '../../services/api';

const useDietRequestsData = ({ isAuthenticated, authCode }) => {
  // State for API data
  const [apiResponseData, setApiResponseData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [countries, setCountries] = useState([]);
  const [labels, setLabels] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25
  });

  // Applied filters (used for API calls)
  const [serverFilters, setServerFilters] = useState({
    user: '',
    country: '',
    profile: '',
    fromDate: '',
    toDate: '',
    labels: [],
    recommendationId: '',
    test: false
  });

  // Form input values (separate from applied filters)
  const [filterInputs, setFilterInputs] = useState({
    user: '',
    country: '',
    profile: '',
    fromDate: '',
    toDate: '',
    labels: [],
    recommendationId: '',
    test: false
  });

  // Process data for TanStack Table - return original API structure
  const data = useMemo(() => {
    return recommendations;
  }, [recommendations]);

  // Fetch data with server-side parameters
  const fetchRecommendations = async () => {
    if (isAuthenticated && authCode) {
      try {
        setLoading(true);
        setError(null);

        const params = {
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          ...serverFilters,
          test: serverFilters.test ? 1 : 0, // Convert boolean to API format
          labels: Array.isArray(serverFilters.labels)
            ? serverFilters.labels.map(label => label.name).join(',')
            : '' // Convert selected labels to comma-separated names
        };

        const data = await recommendationsService.getRecommendationsPageData(authCode, params);

        if (data && data.recommendations) {
          setApiResponseData(data); // Store full response for accessing root-level fields

          // Extract root-level metadata from API response (only global properties)
          const rootMetadata = {
            envName: data.envName,
            admin: data.admin,
            countries: data.countries
          };

          // Merge root metadata with each recommendation (recommendation already has analysisUrl, siteBaseUrlPrefix, etc.)
          const recommendationsWithMetadata = data.recommendations.map(rec => ({
            ...rec,
            ...rootMetadata
          }));

          setRecommendations(recommendationsWithMetadata);
          setTotalCount(data.totalCount || 0);
          setPageCount(data.pageCount || 1);
          setCountries(data.countries || []);
          setLabels(data.labels || []);
          setProfiles(data.profiles || []);
        } else {
          setApiResponseData(null);
          setRecommendations([]);
          setTotalCount(0);
          setPageCount(0);
          setCountries([]);
          setLabels([]);
          setProfiles([]);
        }
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        
        // Extract error message from API response
        let errorMessage = 'Failed to load recommendations';
        
        if (err.response?.data) {
          const responseData = err.response.data;
          
          // Check if response has status and message (API error format)
          if (responseData.status === 'error' && responseData.message) {
            errorMessage = responseData.message;
          } else if (responseData.message) {
            // Fallback to just message field
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            // Fallback if response data is a string
            errorMessage = responseData;
          }
        } else if (err.message) {
          // Network or other error
          errorMessage = `Network error: ${err.message}`;
        }
        
        setError(errorMessage);
        setApiResponseData(null);
        setRecommendations([]);
        setTotalCount(0);
        setPageCount(0);
        setCountries([]);
        setLabels([]);
        setProfiles([]);
      } finally {
        setLoading(false);
      }
    } else if (!isAuthenticated) {
      setApiResponseData(null);
      setRecommendations([]);
      setTotalCount(0);
      setPageCount(0);
      setCountries([]);
      setLabels([]);
      setProfiles([]);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, [isAuthenticated, authCode, pagination.pageIndex, pagination.pageSize, serverFilters]);

  // Handle input changes (doesn't trigger API call)
  const handleInputChange = (field, value) => {
    setFilterInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters (triggers API call)
  const applyFilters = () => {
    setServerFilters(filterInputs);
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    const emptyFilters = {
      user: '',
      country: '',
      profile: '',
      fromDate: '',
      toDate: '',
      labels: [],
      recommendationId: '',
      test: false
    };

    setServerFilters(emptyFilters);
    setFilterInputs(emptyFilters);
    // Reset to first page when filters are cleared
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
  };

  // Helper function to parse date string into Date object
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    try {
      // Parse DD/MM/YYYY 12:00AM format
      const parts = dateString.replace(' 12:00AM', '').split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]); // Year, Month-1, Day
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Helper function to format Date object to DD/MM/YYYY 12:00AM format
  const formatDateForAPI = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' 12:00AM';
  };

  return {
    // Data
    data,
    recommendations,
    totalCount,
    pageCount,
    countries,
    labels,
    profiles,
    loading,
    error,
    
    // Pagination
    pagination,
    setPagination,
    
    // Filters
    serverFilters,
    filterInputs,
    handleInputChange,
    applyFilters,
    clearAllFilters,
    
    // Date helpers
    parseDateString,
    formatDateForAPI,
    
    // API response
    apiResponseData
  };
};

export default useDietRequestsData;